// Order sets, apply side (PROD-2712): the PURE rules. Everything here is a function of its inputs,
// so the whole apply decision (what lands, what waits, what is listed, what is refused) is testable
// without a page. The hook `use-order-set-apply.ts` owns the state and the network; the rail and
// the canvas blocks only render.
//
// HOW A SET ITEM BECOMES A DRAFT ORDER. An item's `details` is the sanitised original
// PatientAssessment entry, minus its code object and minus its `childMedicalCode` — both live in
// `item.code` / as separate items (Scrips.Patient `PROD-2709-MigrateBundlesToOrderSets.sql`, step 2,
// is the authority for which list and which code key each type came from). So the draft is rebuilt
// through the Settings builder's own `itemToPayload` (PROD-2714), which puts the code object back
// under its original key and runs the SAME mapper a reloaded assessment goes through
// (`patientAssessmentToCanvas`). No per-type mapper is duplicated here: a medication comes out with
// exactly the payload a reload of that medication would produce, and what the builder does not keep
// (patient, dates, notes, order and lot numbers) is not kept here either.
import type { CodeSearchResult } from '@scripsteam/scrips-signal-ds';
import type { OrderType, PlanOrder } from '../lib/to-patient-assessment';
import { itemToPayload } from '@/settings/components/order-sets/order-set-item-details';
import { SYNTHETIC_ID_PREFIX, withoutServerDates, type CodedOrderPayload } from '../lib/order-payload';
import type { EducationArticle } from '../lib/to-education-article';
import { findDuplicateOrders, sameArticle, type ExistingOrder } from './order-set-duplicates';
import type { OrderSetDiagnosis } from '@/types/order-set-api';
import {
  isItemOrderable,
  type OrderSetApplyDetail,
  type OrderSetApplyItem,
} from '@/types/order-set-apply-api';

// ── Codes ───────────────────────────────────────────────────────────────────────────────────────

/** A coding as the canvas blocks print it. */
export interface OrderCodeKey {
  system?: string | null;
  code?: string | null;
}

const norm = (v: string | null | undefined) => (v ?? '').trim().toLowerCase();

// "Is this item already ordered on this visit?" lives in `order-set-duplicates.ts` (PROD-2721, the
// AC2 rules per order type). `planOrderSetApplication` below is its only caller on apply.

/** The order's own coding, read off its payload (the same fields the row prints its code from). */
export function orderCodeOf(payload: CodedOrderPayload | undefined): OrderCodeKey | null {
  if (!payload) return null;
  switch (payload.kind) {
    case 'medication':
      return { system: payload.medicationCode.system, code: payload.medicationCode.code };
    case 'vaccination':
      return { system: payload.entry.vaccine?.system, code: payload.entry.vaccine?.code };
    case 'lab':
    case 'imaging':
    case 'procedure':
      return { system: payload.code.system, code: payload.code.code };
    case 'referral':
      return null;
  }
}

// ── Item → draft ────────────────────────────────────────────────────────────────────────────────

/** A set item rebuilt as a draft order, ready for the canvas's own attach path. */
export interface DraftOrder {
  itemId: string;
  planOrder: PlanOrder;
  payload: CodedOrderPayload;
}

/** The order type the canvas files an item's draft under (the DS vocabulary, not the set's). */
export function canvasTypeOf(type: OrderSetApplyItem['type']): OrderType | null {
  switch (type) {
    case 'medication':
      return 'medication';
    case 'vaccine':
      return 'vaccination';
    case 'lab':
      return 'lab';
    case 'imaging':
      return 'imaging';
    case 'procedure':
      return 'procedure';
    default:
      return null;
  }
}

/**
 * Rebuild one set item as a draft order. The payload comes from `itemToPayload` — the SAME
 * details → A/P mapping the Settings builder opens an item's order form with (PROD-2714), which
 * itself runs `patientAssessmentToCanvas`, the mapper a reloaded assessment uses. `orderId` must be
 * a SYNTHETIC id (see `mintOrderSetOrderId`) so the save creates a fresh child and never adopts a
 * server id. Null for a type the canvas cannot hold as an order (referral, other, education).
 */
export function orderSetItemToDraft(item: OrderSetApplyItem, orderId: string): DraftOrder | null {
  const type = canvasTypeOf(item.type);
  if (!type) return null;
  const stored = itemToPayload(item);
  if (!stored) return null;
  // A set-applied order is NEW on this visit (PROD-2854): no date from the stored item rides along.
  // The set builder already strips dates on save; this holds for an item stored before that, or by
  // another client. The apply time is stamped when the draft is attached (attachOrder →
  // setOrderPayload → stampNewOrderDates), which is also when an optional item ticked later is added.
  const payload = withoutServerDates(stored);
  return {
    itemId: item.itemId,
    planOrder: { id: orderId, type, label: item.code.display || item.code.code, dxIds: [] },
    payload,
  };
}

let mintSeq = 0;
/** A synthetic order id for a set-applied draft (never adopted as a server id by the save). */
export function mintOrderSetOrderId(itemId: string): string {
  mintSeq += 1;
  return `${SYNTHETIC_ID_PREFIX}orderset-${itemId}-${Date.now()}-${mintSeq}`;
}

/** An `education` item as an article for the A/P's existing education path. */
export function orderSetItemToArticle(item: OrderSetApplyItem): EducationArticle | null {
  if (item.type !== 'education') return null;
  const d = item.details ?? {};
  const display = (item.code?.display || (typeof d.display === 'string' ? d.display : '')).trim();
  if (!display) return null;
  return {
    display,
    // New on this visit: the apply time, once, so a later save doesn't re-stamp it (PROD-2854).
    onSetDate: new Date().toISOString(),
    ...(typeof d.articleUrl === 'string' && d.articleUrl ? { articleUrl: d.articleUrl } : {}),
    ...(typeof d.summary === 'string' && d.summary ? { summary: d.summary } : {}),
  };
}

// ── The apply plan ──────────────────────────────────────────────────────────────────────────────

/** An item listed under the diagnosis instead of being added. */
export interface ListedItem {
  itemId: string;
  display: string;
}

export interface DuplicateItem extends ListedItem {
  /** The display of the order already on the visit. */
  existingLabel: string;
}

export interface NotOrderableItem extends ListedItem {
  reason?: string;
}

export interface OrderSetApplyPlan {
  /** Default orders that land now, in the set's order. */
  toAdd: DraftOrder[];
  /** Default `education` items, through the A/P's education path. */
  articles: Array<{ itemId: string; article: EducationArticle }>;
  /** `addByDefault: false` — listed with "Add", never added by the apply. */
  optional: ListedItem[];
  /** Already on the visit — listed beside the existing order with "Order anyway". */
  duplicates: DuplicateItem[];
  /** AC4 — never added. */
  notOrderable: NotOrderableItem[];
  /** A type this client cannot place from a set (referral, other). Listed, never added. */
  unsupported: ListedItem[];
}

export type { ExistingOrder };

/**
 * Decide what applying `detail` to a visit does. `existing` = the orders already on the visit
 * (their label and payload). `existingArticles` = the articles already attached. Duplicates follow
 * the AC2 rules in `order-set-duplicates.ts`, against the visit's orders AND the ones this
 * application adds before them (a set never places the same order twice).
 */
export function planOrderSetApplication(
  detail: OrderSetApplyDetail,
  existing: ExistingOrder[],
  existingArticles: EducationArticle[],
  mintId: (itemId: string) => string = mintOrderSetOrderId,
): OrderSetApplyPlan {
  const plan: OrderSetApplyPlan = { toAdd: [], articles: [], optional: [], duplicates: [], notOrderable: [], unsupported: [] };
  const seenArticles = [...existingArticles];
  const onVisit = [...existing];
  const items = [...(detail.items ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  for (const item of items) {
    const display = item.code?.display || item.code?.code || 'Order';
    if (!isItemOrderable(item)) {
      plan.notOrderable.push({ itemId: item.itemId, display, ...(item.notOrderableReason ? { reason: item.notOrderableReason } : {}) });
      continue;
    }
    if (item.type === 'education') {
      const article = orderSetItemToArticle(item);
      if (!article) {
        plan.unsupported.push({ itemId: item.itemId, display });
        continue;
      }
      if (!item.addByDefault) {
        plan.optional.push({ itemId: item.itemId, display: article.display });
        continue;
      }
      if (seenArticles.some((a) => sameArticle(a, article))) continue; // already attached: nothing to add
      seenArticles.push(article);
      plan.articles.push({ itemId: item.itemId, article });
      continue;
    }
    if (!canvasTypeOf(item.type)) {
      plan.unsupported.push({ itemId: item.itemId, display });
      continue;
    }
    if (!item.addByDefault) {
      plan.optional.push({ itemId: item.itemId, display });
      continue;
    }
    const draft = orderSetItemToDraft(item, mintId(item.itemId));
    if (!draft) {
      plan.unsupported.push({ itemId: item.itemId, display });
      continue;
    }
    const dupOf = findDuplicateOrders(draft.payload, onVisit);
    if (dupOf.length > 0) {
      plan.duplicates.push({ itemId: item.itemId, display, existingLabel: dupOf.join(', ') });
      continue;
    }
    plan.toAdd.push(draft);
    onVisit.push({ label: draft.planOrder.label, payload: draft.payload });
  }
  return plan;
}

// ── Diagnosis ───────────────────────────────────────────────────────────────────────────────────

export interface VisitDx {
  id: string;
  code?: string;
  display: string;
  isPrimary?: boolean;
}

export type DxDecision =
  /** The orders link to this visit diagnosis now. */
  | { kind: 'linked'; dxId: string }
  /** No saved diagnosis is on the visit: the orders wait in "Needs a diagnosis". */
  | { kind: 'needs-dx'; suggested: OrderSetDiagnosis | null };

/**
 * Where a set's orders go.
 *  - dropped onto a diagnosis → that diagnosis;
 *  - applied with + → the set's saved diagnosis when it is on the visit (the visit's primary if it
 *    is one of them, else the first match in the set's own order), preselected;
 *  - no match → "Needs a diagnosis", suggesting the set's first saved diagnosis (if any).
 */
export function decideTargetDx(
  setDiagnoses: OrderSetDiagnosis[],
  visitDx: VisitDx[],
  droppedOnDxId?: string,
): DxDecision {
  if (droppedOnDxId && visitDx.some((d) => d.id === droppedOnDxId)) return { kind: 'linked', dxId: droppedOnDxId };
  const setCodes = setDiagnoses.map((d) => norm(d.code)).filter(Boolean);
  const matches = visitDx.filter((d) => setCodes.includes(norm(d.code)));
  const primary = matches.find((d) => d.isPrimary);
  if (primary) return { kind: 'linked', dxId: primary.id };
  for (const code of setCodes) {
    const hit = matches.find((d) => norm(d.code) === code);
    if (hit) return { kind: 'linked', dxId: hit.id };
  }
  return { kind: 'needs-dx', suggested: setDiagnoses[0] ?? null };
}

/** True when any of the set's saved diagnoses is on the visit (the rail's "matches"). */
export function setMatchesVisit(setDiagnoses: OrderSetDiagnosis[], visitCodes: string[]): boolean {
  const codes = new Set(visitCodes.map(norm).filter(Boolean));
  return setDiagnoses.some((d) => codes.has(norm(d.code)));
}

/** The suggested diagnosis as the canvas's own add path takes it. */
export function suggestedDxAsSearchResult(dx: OrderSetDiagnosis): CodeSearchResult {
  return { id: dx.code, code: dx.code, display: dx.display, system: dx.system } as CodeSearchResult;
}

// ── Copy ────────────────────────────────────────────────────────────────────────────────────────

export const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The rail card's AC4 line: "1 order no longer orderable" / "2 orders no longer orderable". */
export function notOrderableLine(n: number): string {
  return `${plural(n, 'order', 'orders')} no longer orderable`;
}

/**
 * The apply receipt (boards 6 and 6b, verbatim):
 *   "5 orders added from Diabetes follow-up. 3 not ordered are listed under the diagnosis."
 *   "4 orders added from Annual wellness. Link them to a diagnosis."
 */
export function applyReceipt(setName: string, added: number, notOrdered: number, needsDx: boolean): string {
  const head = `${plural(added, 'order', 'orders')} added from ${setName}.`;
  if (needsDx) return `${head} Link ${added === 1 ? 'it' : 'them'} to a diagnosis.`;
  if (notOrdered > 0) {
    return `${head} ${notOrdered} not ordered ${notOrdered === 1 ? 'is' : 'are'} listed under the diagnosis.`;
  }
  return head;
}
